import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Href, useRouter, Link as RouterLink } from 'expo-router';
import { useAtom, useAtomValue } from 'jotai';
import { HatGlasses, GraduationCap, PanelRight, UserSearch } from 'lucide-react-native';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { primum_default_avatar } from '@/assets/images';
import { ChatErrorBoundary } from '@/components/ChatErrorBoundary';
import { ChatLoadError } from '@/components/ChatLoadError';
import { headerItem } from '@/components/HeaderActions';
import { LinkedInquiryBanner } from '@/components/LinkedInquiryBanner';
import { ProductDetailsBar } from '@/components/MedicalOnDemand/ProductDetailsBar';
import { RiveAnimation } from '@/components/RiveAnimation';
import { ChatContent } from '@/components/chat/ChatContent';
import { PostalCodeWithCopy } from '@/components/chat/ChatHeaderContent';
import type { ChatProps } from '@/components/chat/ChatTypes';
import { ClinicalCaseHeaderLine } from '@/components/chat/ClinicalCaseHeaderLine';
import { FadedHorizontalScroll } from '@/components/chat/FadedHorizontalScroll';
import { HeaderMenuButton } from '@/components/headerItems/HeaderMenuButton';
import type { HeaderMenuAction } from '@/components/headerItems/types';
import { inboxSidebarOpenAtom } from '@/components/inbox/inboxSidebarState';
import { SupportThreadPane } from '@/components/supportThread/SupportThreadPane';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Badge, BadgeIcon, BadgeText } from '@/components/ui/badge';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable as GluestackPressable } from '@/components/ui/pressable';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { Tooltip, TooltipContent, TooltipText } from '@/components/ui/tooltip';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import {
  ANONYMOUS_INQUIRY_BADGE_LABEL,
  MENTOR_INQUIRY_BADGE_LABEL,
  NO_EXPERT_INQUIRY_BADGE_LABEL,
} from '@/constants/inquiryDisplay';
import { useInquirySupportAction } from '@/features/support-contact';
import { useRememberedRelays } from '@/hooks/chat/useRememberedRelays';
import {
  useAnonymousInquiryGroupingEnabled,
  useGroupedInquiriesEnabled,
} from '@/hooks/inbox/useGroupedInquiriesEnabled';
import { useInquiryCloseNavigation } from '@/hooks/inbox/useInquiryCloseNavigation';
import { useLinkedSourceConversationId } from '@/hooks/inbox/useLinkedSourceConversationId';
import { useQuestionGroupChatTabs } from '@/hooks/inbox/useQuestionGroupChatTabs';
import { useChat } from '@/hooks/useChat';
import { useChatDraft } from '@/hooks/useChatDraft';
import { useCloseConversation } from '@/hooks/useCloseConversation';
import { useCognitoGroups } from '@/hooks/useCognitoGroups';
import { useConversationKind } from '@/hooks/useConversationKind';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useModTeamName } from '@/hooks/useModTeamName';
import { useProductInfo } from '@/hooks/useProductInfo';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import { useSingleValueSearchParams } from '@/hooks/useSingleValueSearchParams';
import { useCanStartSupportThread } from '@/hooks/useSupportStaffAbilities';
import { isReadOnlyConversation } from '@/services/chat/readOnlyInquiryConversation';
import { type InquiryV2 } from '@/services/getInquiriesV2';
import { isFeedbackActiveAtom, userIdAtom } from '@/services/globalState';
import { segmentClient } from '@/util/analytics';
import { trackInquiryGroupTabSwitched, type InboxTabContext } from '@/util/analyticsEvents';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import { getInquirySubject } from '@/util/getInquirySubject';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';
import {
  getInquiryDisplayParticipant,
  getInquiryTitleExpert,
  isAnonymousPrimumRoutedInquiry,
  isNoExpertInquiry,
} from '@/util/inquiryDisplay';
import {
  getChatGroupTabInquiries,
  getGroupTabLabel,
  getGroupedDefaultConversationId,
  getRelayViewTabTitle,
} from '@/util/inquiryGroups';
import { isSameUserId } from '@/utils/isSameUserId';
import { getModRouteLabel, resolveModGroupRoutes } from '@/utils/modHelpers';

function Empty() {
  const { navigate } = useRouter();

  return (
    <VStack className="w-full flex-1 items-center justify-center bg-background-50">
      <VStack className="mb-6 h-[165px] w-full">
        <RiveAnimation name="GAIN_CLARITY" autoplay />
      </VStack>

      <Text className="text-center font-medium text-typography-900" size="lg">
        Nothing to see here!
      </Text>

      <Text className="mb-6 text-center">
        Once you close an inquiry, you'll be able to reference it here.
      </Text>

      <Button size="sm" variant="outline" onPress={() => navigate('/directory')}>
        <ButtonText>Find an expert</ButtonText>
      </Button>
    </VStack>
  );
}

const pluralize = (count: number, singular: string, plural: string) => {
  return count === 1 ? singular : plural;
};

function Unselected({
  openInquiriesCount,
  inquiriesClosedLastWeekCount,
}: Pick<ChatSectionProps, 'openInquiriesCount' | 'inquiriesClosedLastWeekCount'>) {
  const { navigate } = useRouter();

  return (
    <VStack className="w-full flex-1 items-center justify-center bg-background-50">
      <VStack className="mb-6 h-[165px] w-full">
        <RiveAnimation name="BUILDING_SUPPORT_LIGHT_GREEN" autoplay />
      </VStack>

      <Text className="text-center font-medium text-typography-900" size="lg">
        You've completed {inquiriesClosedLastWeekCount}{' '}
        {pluralize(inquiriesClosedLastWeekCount, 'inquiry', 'inquiries')} this week
      </Text>

      <Text className="mb-6 text-center">
        You have {openInquiriesCount} {pluralize(openInquiriesCount, 'inquiry', 'inquiries')} in
        progress
      </Text>

      <Button size="sm" variant="outline" onPress={() => navigate('/directory')}>
        <ButtonText>Find an expert</ButtonText>
      </Button>
    </VStack>
  );
}

const AVATAR_HEIGHT = {
  height: 48,
  width: 48,
};

type ResolvedConversationData = {
  conversation: ChatProps['conversation'];
  recipient: ChatProps['recipient'];
  conversationStatus: ChatProps['conversationStatus'];
  inquiry: ChatProps['inquiry'];
};

function Chat(
  props: ChatProps & {
    /** The single-inquiry response, which carries the server-resolved sibling group. */
    inquiry: { groupInquiries?: InquiryV2[] };
    allInquiries: InquiryV2[];
    tabSelected: string;
  }
) {
  const {
    userId,
    recipient,
    conversation,
    conversationStatus,
    inquiry,
    allInquiries,
    tabSelected,
  } = props;

  const isInboxGroupedInquiriesEnabled = useGroupedInquiriesEnabled();

  const isAnonymousInquiryGroupingEnabled = useAnonymousInquiryGroupingEnabled();

  const selectedInquiryGroupKey = useMemo(
    () => `parent-${inquiry.parentId ?? inquiry.id}`,
    [inquiry.id, inquiry.parentId]
  );

  const groupedDefaultConversationId = useMemo(
    () =>
      getGroupedDefaultConversationId({
        serverGroupInquiries: inquiry.groupInquiries,
        listInquiries: allInquiries,
        conversationId: conversation.id,
        isGroupedEnabled: isInboxGroupedInquiriesEnabled,
        userId,
        groupKeyFallback: selectedInquiryGroupKey,
        isAnonymousInquiryGroupingEnabled,
      }),
    [
      allInquiries,
      conversation.id,
      inquiry.groupInquiries,
      isInboxGroupedInquiriesEnabled,
      selectedInquiryGroupKey,
      userId,
      isAnonymousInquiryGroupingEnabled,
    ]
  );

  const [activeConversationId, setActiveConversationId] = useState(groupedDefaultConversationId);

  const [resolvedConversationData, setResolvedConversationData] =
    useState<ResolvedConversationData | null>(() => {
      if (groupedDefaultConversationId !== conversation.id) {
        return null;
      }

      return {
        conversation,
        recipient,
        conversationStatus,
        inquiry,
      };
    });

  const syncGroupedConversationState = useCallback(
    (nextConversationId: string) => {
      setActiveConversationId(nextConversationId);

      setResolvedConversationData((previousResolvedConversationData) => {
        if (nextConversationId === conversation.id) {
          return {
            conversation,
            recipient,
            conversationStatus,
            inquiry,
          };
        }

        if (previousResolvedConversationData?.conversation.id === nextConversationId) {
          return previousResolvedConversationData;
        }

        return null;
      });
    },
    [conversation, conversation.id, conversationStatus, inquiry, recipient]
  );

  const {
    conversation: activeConversation,
    recipient: activeRecipient,
    conversationStatus: activeConversationStatus,
    inquiry: activeInquiry,
    error: activeConversationError,
    isLoading: isLoadingActiveConversation,
    retry: retryActiveConversation,
  } = useChat(activeConversationId);

  useEffect(() => {
    if (!activeConversation || !activeRecipient || !activeConversationStatus || !activeInquiry) {
      return;
    }

    setResolvedConversationData({
      conversation: activeConversation,
      recipient: activeRecipient,
      conversationStatus: activeConversationStatus,
      inquiry: activeInquiry,
    });
  }, [activeConversation, activeRecipient, activeConversationStatus, activeInquiry]);

  /**
   * Only a genuinely different default may move the selection. This effect also
   * depends on the sync callback, whose identity changes whenever the inquiry or
   * recipient objects are replaced — and TanStack Query replaces them on every
   * refetch, including the ones the Twilio listeners trigger on each incoming
   * message. Without this guard, a message arriving while the reader is typing
   * snapped them back to the default tab and swapped the draft out from under
   * them, with no error to explain it.
   */
  const appliedDefaultConversationIdRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (appliedDefaultConversationIdRef.current === groupedDefaultConversationId) {
      return;
    }

    appliedDefaultConversationIdRef.current = groupedDefaultConversationId;

    syncGroupedConversationState(groupedDefaultConversationId);
  }, [groupedDefaultConversationId, syncGroupedConversationState]);

  const currentConversation = resolvedConversationData?.conversation ?? conversation;

  /**
   * Mirrors the fullscreen route: while the selected sibling is still loading,
   * the rendered conversation is the previously selected one, so the composer
   * must not send into it.
   */
  const isResolvingActiveConversation =
    !!activeConversationId && currentConversation.id !== activeConversationId;

  const currentRecipient = resolvedConversationData?.recipient ?? recipient;

  const currentConversationStatus =
    resolvedConversationData?.conversationStatus ?? conversationStatus;

  const currentInquiry = resolvedConversationData?.inquiry ?? inquiry;

  const { message } = useSingleValueSearchParams(['message'] as const);

  const { draftMessage, setDraftMessage, clearDraft } = useChatDraft(
    currentConversation.id,
    userId,
    message || undefined
  );

  const hasClearedInitialMessageRef = useRef(false);

  const [chatKeyboardOffset, setChatKeyboardOffset] = useState<number | undefined>(undefined);

  const chatShellRef = useRef<React.ComponentRef<typeof VStack>>(null);

  const currentDisplayParticipant = getInquiryDisplayParticipant(
    currentInquiry,
    currentRecipient,
    isAnonymousInquiryGroupingEnabled
  );

  const currentTitleExpert = getInquiryTitleExpert(
    currentInquiry,
    isAnonymousInquiryGroupingEnabled
  );

  const showAnonymousRoutingChrome = isAnonymousPrimumRoutedInquiry(currentInquiry);

  const { name } = getProfileNameAndInitials(currentDisplayParticipant);

  const { navigate, setParams } = useRouter();

  /**
   * Clears the `message` query param once useChatDraft has consumed it as the composer preload,
   * so a refresh doesn't re-preload and overwrite edits. The hook captures the preload
   * synchronously on mount, so clearing the param here can't race it.
   */
  useEffect(() => {
    if (!message || hasClearedInitialMessageRef.current) {
      return;
    }

    hasClearedInitialMessageRef.current = true;

    setParams({ message: undefined });
  }, [message, setParams]);

  const isFullscreenEnabled = useFeatureFlag(FEATURE_FLAGS.INQUIRY_FULLSCREEN_CONVERSATION);

  const isSidebarEnabled = useFeatureFlag(FEATURE_FLAGS.INBOX_SIDEBAR);

  const { productDisplay } = useProductInfo(currentConversation);

  const isCompactLayout = useIsCompactLayout();

  const isCompactWebLayout = isWeb && isCompactLayout;

  const [, setIsSidebarOpen] = useAtom(inboxSidebarOpenAtom);

  const isAuthorView = isSameUserId(inquiry.userId, userId);

  const isAskAnonymouslyRelayView =
    (showAnonymousRoutingChrome || isAnonymousPrimumRoutedInquiry(inquiry)) && !isAuthorView;

  const showGroupedAnonymousHint =
    (showAnonymousRoutingChrome || isAnonymousPrimumRoutedInquiry(inquiry)) &&
    isAnonymousInquiryGroupingEnabled;

  const showMentorBadge = currentInquiry.title?.type === 'mentor_inquiry';

  /**
   * Recipient (MOD expert) view: surface the submitter as the chat-header
   * participant so the avatar/name describe who they're answering. Submitter
   * view keeps the existing other-participant resolution (the MOD expert).
   */
  const otherParticipantForHeader = isAuthorView ? currentDisplayParticipant : inquiry.author;

  const headerParticipant = isAskAnonymouslyRelayView
    ? currentRecipient
    : otherParticipantForHeader;

  const { name: headerParticipantName } = getProfileNameAndInitials(headerParticipant);

  /**
   * Gate the team-name rebrand on `MOD_DIRECT_TO_MSL`. When the flag is OFF
   * the chat header falls back to the legacy team name so users not in the
   * rollout see the pre-MSL labeling.
   */
  const modTeamName = useModTeamName(productDisplay?.displayName);

  const baseDisplayName = isAuthorView ? (modTeamName ?? name) : headerParticipantName;

  const titleExpertTextClassName = currentTitleExpert
    ? twJoin(
        isAskAnonymouslyRelayView
          ? 'font-medium text-sm lg:text-xs xl:text-sm text-typography-600'
          : 'text-base lg:text-sm xl:text-base'
      )
    : 'text-base lg:text-sm xl:text-base';

  const relaySourceText = null;

  const isSupportConversation = currentInquiry.origin === 'primum_support';

  const showSupportBadge = isSupportConversation;

  /**
   * Fellows Corner submits every mentor inquiry under one fixed cancer-focus
   * subject, so it never varies inquiry to inquiry and can't help anyone scan
   * their inbox. The participant's name is the one fact that actually
   * differs, so it takes the subject slot the same way a support
   * conversation's does.
   */
  const displaySubject =
    isSupportConversation || showMentorBadge
      ? headerParticipantName
      : (productDisplay?.displaySubject ??
        getInquirySubject(currentConversation.attributes as InquiryV2['title']));

  const questionGroupTabs = useQuestionGroupChatTabs(conversation.id);

  const questionGroupTabLabels = useMemo(
    () => new Map(questionGroupTabs?.map((tab) => [tab.thread.conversationId, tab.label])),
    [questionGroupTabs]
  );

  /** The card's tabs, when the chat was opened from one, otherwise the tabs of one send. */
  const isGroupTabsEnabled = isInboxGroupedInquiriesEnabled || questionGroupTabs !== undefined;

  /**
   * "For Dr. X" names the expert a submitter's fan-out thread is meant for. A card's tabs are the
   * expert threads themselves, so they carry the expert's name alone, as on the mobile route.
   */
  const showForPrefix = isAskAnonymouslyRelayView && questionGroupTabs === undefined;

  /**
   * The experts each sibling thread was relayed to. The tabs come partly from the inbox list, which
   * does not carry relays, so the names are read from the inquiry detail the chat already loaded,
   * and remembered so a tab switch does not blank them while the next detail loads.
   */
  const relayThreads = useMemo(
    () => [activeInquiry, ...(activeInquiry?.groupInquiries ?? [])],
    [activeInquiry]
  );

  const relaysByConversation = useRememberedRelays(relayThreads);

  const inquiryGroupInquiries = useMemo(() => {
    if (questionGroupTabs) {
      return questionGroupTabs.map((tab) => tab.thread);
    }

    if (!isInboxGroupedInquiriesEnabled) {
      return [];
    }

    return getChatGroupTabInquiries({
      serverGroupInquiries: activeInquiry?.groupInquiries,
      listInquiries: allInquiries,
      conversationId: conversation.id,
      userId,
      groupKeyFallback: selectedInquiryGroupKey,
      isAnonymousInquiryGroupingEnabled,
    });
  }, [
    activeInquiry?.groupInquiries,
    allInquiries,
    conversation.id,
    isInboxGroupedInquiriesEnabled,
    questionGroupTabs,
    selectedInquiryGroupKey,
    userId,
    isAnonymousInquiryGroupingEnabled,
  ]);

  const groupRouteAssignment = useMemo(
    () => resolveModGroupRoutes(inquiryGroupInquiries),
    [inquiryGroupInquiries]
  );

  const inquiryRoute = groupRouteAssignment.get(currentInquiry.conversationId) ?? null;

  /**
   * On MSL-routed siblings the recipient sees the submitter's postal code
   * inline with their name, alongside a copy-to-clipboard affordance — so
   * the MOD expert can look up the territory on J&J Medical Connect.
   */
  const recipientPostalCode =
    !isAuthorView && inquiryRoute === 'msl' ? currentInquiry.title?.postalCode : undefined;

  /**
   * Submitter view substitutes the route label as the secondary header line
   * (so they see the active route). Recipient view keeps the submitter name;
   * the route appears via grouped tabs or `routeCaption`, and the postal
   * code (when present) is rendered separately so it can carry a copy
   * button.
   */
  const displayName =
    isAuthorView && inquiryRoute ? getModRouteLabel(inquiryRoute) : baseDisplayName;

  const shouldRenderDisplayName = Boolean(
    displayName?.trim() && displayName.trim() !== displaySubject?.trim()
  );

  /** True when a name renders in the header, so the anonymity badge sits inline beside it rather than on its own line. */
  const headerNameLineVisible =
    !showSupportBadge && !isSupportConversation && shouldRenderDisplayName;

  /**
   * Recipient side: surface the active route as a muted caption under the
   * subject. Renders for both single-sibling cards and multi-sibling groups
   * (where it tracks the currently-selected tab). Submitter side already
   * carries the route via `displayName`.
   */
  const routeCaption = !isAuthorView && inquiryRoute ? getModRouteLabel(inquiryRoute) : undefined;

  useLayoutEffect(() => {
    if (!isGroupTabsEnabled) {
      return;
    }

    if (!activeConversationId) {
      return;
    }

    const hasActiveConversationInGroup = inquiryGroupInquiries.some(
      (relatedInquiry) => relatedInquiry.conversationId === activeConversationId
    );

    if (hasActiveConversationInGroup) {
      return;
    }

    syncGroupedConversationState(groupedDefaultConversationId);
  }, [
    activeConversationId,
    groupedDefaultConversationId,
    inquiryGroupInquiries,
    isGroupTabsEnabled,
    syncGroupedConversationState,
  ]);

  const {
    closeMenuAction,
    headerRightAction,
    topBanner,
    isAuthor,
    closedConversation,
    webInquiryModals,
  } = useCloseConversation({
    userId,
    conversation: currentConversation,
    recipientId: currentRecipient.id.toString(),
    recipientName: getProfileNameAndInitials(currentDisplayParticipant).formalName,
    conversationStatus: currentConversationStatus,
    inquiryFeedbackStatus: currentInquiry.feedbackStatus ?? 'Pending',
    authorId: currentInquiry.userId,
    inquiry: currentInquiry,
    isAskAnonymouslyRelayView,
  });

  const contactSupportAction = useInquirySupportAction({
    inquiry: {
      inquiryId: currentInquiry.id,
      conversationId: currentConversation.id,
      title: displaySubject || name || 'This inquiry',
    },
    isReferenceable:
      isSameUserId(currentInquiry.userId, userId) ||
      isSameUserId(currentInquiry.recipient?.id, userId),
    isHidden: isReadOnlyConversation(currentConversation),
  });

  const isFeedbackActive = useAtomValue(isFeedbackActiveAtom);

  useInquiryCloseNavigation({
    currentConversationId: currentConversation.id,
    currentConversationStatus,
    groupedRoutes: inquiryGroupInquiries.map((inq) => ({
      conversationId: inq.conversationId,
      inquiryStatus: inq.inquiryStatus,
    })),
    tabSelected,
    isGroupedEnabled: isGroupTabsEnabled,
  });

  const refConversationId: string | undefined = (
    currentInquiry as Partial<InquiryV2> & {
      referenceInquiryIds?: string[];
    }
  )?.referenceInquiryIds?.[0];

  const linkedSourceConversationId = useLinkedSourceConversationId({
    referenceConversationId: refConversationId,
    recipientId: currentInquiry.recipient?.id,
  });

  const { isPrimumUser } = useCognitoGroups();

  const showNoExpertBadge = isPrimumUser && !isAuthorView && isNoExpertInquiry(currentInquiry);

  const onFullscreenPress = () => {
    navigate(`/inbox/chat/${currentConversation.id}?tab=${tabSelected}` as Href);
  };

  const menuActions: HeaderMenuAction[] = [
    { ...closeMenuAction, isHidden: closeMenuAction.isHidden || !headerRightAction },
    {
      key: 'open-fullscreen',
      label: 'Open in fullscreen',
      sfIcon: 'arrow.up.left.and.arrow.down.right',
      isHidden: !isFullscreenEnabled,
      onPress: onFullscreenPress,
    },
    contactSupportAction,
  ];

  const isWaitingForGroupedInitialContent =
    isInboxGroupedInquiriesEnabled &&
    activeConversationId !== conversation.id &&
    resolvedConversationData === null;

  if (isWaitingForGroupedInitialContent && isLoadingActiveConversation && !isFeedbackActive) {
    return (
      <VStack className="flex-1">
        <Spinner size="large" className="absolute bottom-0 left-0 right-0 top-0 flex-1" />
      </VStack>
    );
  }

  return (
    <VStack className="flex-1 pb-0">
      <HStack className="z-10 min-h-20 w-full items-center justify-between border-b border-outline-100 bg-background-0 px-7 py-3">
        <RouterLink
          href={{ pathname: '/profile/[id]', params: { id: headerParticipant.id } }}
          asChild>
          <GluestackPressable
            className="min-w-0 shrink rounded-md data-[focus-visible=true]:web:ring-inset"
            onPress={() => {
              const trackPromise = segmentClient.track(ANALYTICS_EVENTS.PROFILE_VIEWED, {
                source: 'chat_header',
              });

              if (trackPromise && typeof trackPromise.catch === 'function') {
                trackPromise.catch(() => undefined);
              }
            }}>
            <HStack space="sm" className="min-w-0 flex-1 items-center">
              <Avatar style={AVATAR_HEIGHT}>
                <AvatarImage
                  source={getAvatarUrl(headerParticipant.avatar, true) || primum_default_avatar}
                />
              </Avatar>
              <VStack className="min-w-0 shrink">
                {showSupportBadge ? (
                  <Badge
                    size="md"
                    action="info"
                    variant="outline"
                    className="mb-1 self-start border-info-700 bg-background-0 px-2">
                    <BadgeText size="md" className="normal-case text-info-700">
                      Primum Support
                    </BadgeText>
                  </Badge>
                ) : null}
                {headerNameLineVisible ? (
                  <HStack className="min-w-0 flex-wrap items-center gap-2">
                    <Text className={titleExpertTextClassName}>{displayName || ''}</Text>
                    {showGroupedAnonymousHint ? (
                      <Badge
                        size="md"
                        action="info"
                        variant="outline"
                        className="gap-1.5 border-info-700 bg-background-0 px-2"
                        accessibilityLabel="Anonymous inquiry coordinated by Ask Anonymously">
                        <BadgeIcon as={HatGlasses} size="sm" className="text-info-700" />
                        <BadgeText size="md" className="normal-case text-info-700">
                          {ANONYMOUS_INQUIRY_BADGE_LABEL}
                        </BadgeText>
                      </Badge>
                    ) : null}
                    {showMentorBadge ? (
                      <Badge
                        size="md"
                        action="info"
                        variant="outline"
                        className="gap-1.5 border-info-700 bg-background-0 px-2"
                        accessibilityLabel="Mentorship inquiry submitted through Ask a Mentor">
                        <BadgeIcon as={GraduationCap} size="sm" className="text-info-700" />
                        <BadgeText size="md" className="normal-case text-info-700">
                          {MENTOR_INQUIRY_BADGE_LABEL}
                        </BadgeText>
                      </Badge>
                    ) : null}
                    {showNoExpertBadge ? (
                      <Badge
                        size="md"
                        action="muted"
                        variant="outline"
                        className="gap-1.5 border-outline-300 bg-background-0 px-2"
                        accessibilityLabel="Submitted without selecting an expert">
                        <BadgeIcon as={UserSearch} size="sm" className="text-typography-700" />
                        <BadgeText size="md" className="normal-case text-typography-700">
                          {NO_EXPERT_INQUIRY_BADGE_LABEL}
                        </BadgeText>
                      </Badge>
                    ) : null}
                    <PostalCodeWithCopy postalCode={recipientPostalCode} />
                  </HStack>
                ) : null}
                {showGroupedAnonymousHint && !headerNameLineVisible ? (
                  <Badge
                    size="md"
                    action="info"
                    variant="outline"
                    className="mb-1.5 gap-1.5 self-start border-info-700 bg-background-0 px-2"
                    accessibilityLabel="Anonymous inquiry coordinated by Ask Anonymously">
                    <BadgeIcon as={HatGlasses} size="sm" className="text-info-700" />
                    <BadgeText size="md" className="normal-case text-info-700">
                      {ANONYMOUS_INQUIRY_BADGE_LABEL}
                    </BadgeText>
                  </Badge>
                ) : null}
                {showMentorBadge && !headerNameLineVisible ? (
                  <Badge
                    size="md"
                    action="info"
                    variant="outline"
                    className="mb-1.5 gap-1.5 self-start border-info-700 bg-background-0 px-2"
                    accessibilityLabel="Mentorship inquiry submitted through Ask a Mentor">
                    <BadgeIcon as={GraduationCap} size="sm" className="text-info-700" />
                    <BadgeText size="md" className="normal-case text-info-700">
                      {MENTOR_INQUIRY_BADGE_LABEL}
                    </BadgeText>
                  </Badge>
                ) : null}
                {showNoExpertBadge && !headerNameLineVisible ? (
                  <Badge
                    size="md"
                    action="muted"
                    variant="outline"
                    className="mb-1.5 gap-1.5 self-start border-outline-300 bg-background-0 px-2"
                    accessibilityLabel="Submitted without selecting an expert">
                    <BadgeIcon as={UserSearch} size="sm" className="text-typography-700" />
                    <BadgeText size="md" className="normal-case text-typography-700">
                      {NO_EXPERT_INQUIRY_BADGE_LABEL}
                    </BadgeText>
                  </Badge>
                ) : null}
                {!isAskAnonymouslyRelayView ? (
                  <Heading className="min-w-0 break-words text-lg lg:text-base xl:text-lg">
                    {displaySubject}
                  </Heading>
                ) : null}
                <ClinicalCaseHeaderLine conversationId={currentConversation.id} />
                {routeCaption ? (
                  <Text size="sm" className="text-typography-600" numberOfLines={1} isTruncated>
                    {routeCaption}
                  </Text>
                ) : null}
                {relaySourceText ? (
                  <Text size="xs" className="mt-1 text-typography-700">
                    {relaySourceText}
                  </Text>
                ) : null}
              </VStack>
            </HStack>
          </GluestackPressable>
        </RouterLink>
        <HStack space="sm" className="shrink-0">
          {menuActions.some((action) => !action.isHidden) ? (
            <HeaderMenuButton item={headerItem.inquiryActions(menuActions)} />
          ) : null}
          {isWeb && isSidebarEnabled ? (
            <Tooltip
              placement="bottom"
              trigger={(triggerProps: Record<string, unknown>) => (
                <GluestackPressable
                  {...triggerProps}
                  hitSlop={8}
                  onPress={() => setIsSidebarOpen((prev) => !prev)}
                  accessibilityLabel="Toggle panel"
                  className="rounded-md p-1.5 data-[hover=true]:bg-secondary-50 data-[active=true]:bg-secondary-100">
                  <Icon as={PanelRight} size="lg" className="text-secondary-500" />
                </GluestackPressable>
              )}>
              <TooltipContent>
                <TooltipText>Toggle panel</TooltipText>
              </TooltipContent>
            </Tooltip>
          ) : null}
        </HStack>
      </HStack>

      {productDisplay?.productInfo && (
        <ProductDetailsBar
          product={productDisplay.productInfo}
          products={productDisplay.products}
        />
      )}

      {inquiryGroupInquiries.length > 1 ? (
        <VStack
          className={twJoin(
            'border-b border-outline-100 bg-background-0',
            isAskAnonymouslyRelayView ? 'pt-3' : ''
          )}>
          {isAskAnonymouslyRelayView ? (
            <Text size="sm" className="px-7 font-medium text-secondary-700">
              {`Replying to ${headerParticipantName}${displaySubject ? ` · ${displaySubject}` : ''}`}
            </Text>
          ) : null}
          {/* {showGroupedAnonymousHint ? (
            <Text size="xs" className="pl-2 pr-7 pt-1 text-info-700">
              Private, separate conversations
            </Text>
          ) : null} */}
          {isCompactWebLayout ? (
            <VStack className="gap-2 px-4 py-2">
              {inquiryGroupInquiries.map((related) => {
                const relatedFallbackParticipant =
                  related.author.id === Number(userId) ? related.recipient : related.author;

                const relatedParticipant = getInquiryDisplayParticipant(
                  related,
                  relatedFallbackParticipant,
                  isAnonymousInquiryGroupingEnabled
                );

                const relatedRoute = groupRouteAssignment.get(related.conversationId);

                const relatedName =
                  questionGroupTabLabels.get(related.conversationId) ??
                  (relatedRoute
                    ? getModRouteLabel(relatedRoute)
                    : getGroupTabLabel({
                        firstName: relatedParticipant.firstName,
                        lastName: relatedParticipant.lastName,
                        identifiesAsDoctor: relatedParticipant.identifiesAsDoctor,
                      }));

                const isCurrent = related.conversationId === activeConversationId;

                const label = showForPrefix
                  ? getRelayViewTabTitle(
                      relaysByConversation.get(related.conversationId),
                      relatedName
                    )
                  : relatedName;

                const switchConversation = () => {
                  if (isCurrent) {
                    return;
                  }

                  Promise.resolve(
                    trackInquiryGroupTabSwitched({
                      fromConversationId: activeConversationId,
                      toConversationId: related.conversationId,
                      inquiryParentId: currentInquiry.parentId ?? currentInquiry.id,
                      inquiryGroupSize: inquiryGroupInquiries.length,
                      inquiryGroupsEnabled: isInboxGroupedInquiriesEnabled,
                      tabGrouping: questionGroupTabs ? 'question' : 'send',
                      inboxTab: tabSelected as InboxTabContext,
                    })
                  ).catch(() => undefined);

                  setActiveConversationId(related.conversationId);
                };

                return (
                  <GluestackPressable
                    key={related.conversationId}
                    onPress={switchConversation}
                    className={twJoin(
                      'rounded-lg border px-3 py-2.5',
                      isCurrent
                        ? 'border-outline-200 bg-background-50'
                        : 'border-transparent bg-background-0'
                    )}>
                    <Text
                      className={twJoin(
                        'text-left text-sm',
                        isCurrent
                          ? 'font-semibold text-typography-900'
                          : 'font-medium text-typography-700'
                      )}
                      numberOfLines={3}>
                      {label}
                    </Text>
                  </GluestackPressable>
                );
              })}
            </VStack>
          ) : (
            <FadedHorizontalScroll minContentWidth="100%">
              <HStack className="-ml-1 justify-start gap-1 px-7">
                {inquiryGroupInquiries.map((related) => {
                  const relatedFallbackParticipant =
                    related.author.id === Number(userId) ? related.recipient : related.author;

                  const relatedParticipant = getInquiryDisplayParticipant(
                    related,
                    relatedFallbackParticipant,
                    isAnonymousInquiryGroupingEnabled
                  );

                  const relatedRoute = groupRouteAssignment.get(related.conversationId);

                  const relatedName =
                    questionGroupTabLabels.get(related.conversationId) ??
                    (relatedRoute
                      ? getModRouteLabel(relatedRoute)
                      : getGroupTabLabel({
                          firstName: relatedParticipant.firstName,
                          lastName: relatedParticipant.lastName,
                          identifiesAsDoctor: relatedParticipant.identifiesAsDoctor,
                        }));

                  const isCurrent = related.conversationId === activeConversationId;

                  /** 40px visible height for a tighter band; the vertical hitSlop below restores a 48px tap target. */
                  const relatedTabClassName =
                    'h-10 min-w-[132px] items-center justify-center rounded-none border-b-2 px-4 transition-colors duration-200 ease-out data-[hover=true]:bg-transparent data-[active=true]:bg-transparent';

                  return (
                    <Button
                      key={related.conversationId}
                      size="xs"
                      action="default"
                      variant="solid"
                      hitSlop={{ top: 4, bottom: 4 }}
                      className={twJoin(
                        relatedTabClassName,
                        isCurrent
                          ? 'border-b-primary-500 bg-transparent data-[hover=true]:border-b-primary-500 data-[active=true]:border-b-primary-500'
                          : 'border-b-transparent bg-transparent'
                      )}
                      onPress={() => {
                        if (isCurrent) {
                          return;
                        }

                        Promise.resolve(
                          trackInquiryGroupTabSwitched({
                            fromConversationId: activeConversationId,
                            toConversationId: related.conversationId,
                            inquiryParentId: currentInquiry.parentId ?? currentInquiry.id,
                            inquiryGroupSize: inquiryGroupInquiries.length,
                            inquiryGroupsEnabled: isInboxGroupedInquiriesEnabled,
                            tabGrouping: questionGroupTabs ? 'question' : 'send',
                            inboxTab: tabSelected as InboxTabContext,
                          })
                        ).catch(() => undefined);

                        setActiveConversationId(related.conversationId);
                      }}>
                      <Text
                        className={twJoin(
                          'text-sm no-underline',
                          isCurrent
                            ? 'font-semibold text-primary-600'
                            : 'font-medium text-typography-700'
                        )}>
                        {showForPrefix
                          ? getRelayViewTabTitle(
                              relaysByConversation.get(related.conversationId),
                              relatedName
                            )
                          : relatedName}
                      </Text>
                    </Button>
                  );
                })}
              </HStack>
            </FadedHorizontalScroll>
          )}
        </VStack>
      ) : null}

      {refConversationId && isPrimumUser ? (
        <LinkedInquiryBanner
          conversationId={refConversationId}
          loggedInUserId={userId}
          label="Linked from"
          onView={(id: string) => navigate(`/inbox?id=${linkedSourceConversationId ?? id}`)}
          containerClassName="items-start justify-start border-t border-outline-100 bg-background-0 px-7 py-2"
        />
      ) : null}

      {topBanner}

      {webInquiryModals}

      {activeConversationError ? (
        <ChatLoadError errorType={activeConversationError.type} onRetry={retryActiveConversation} />
      ) : (
        <VStack
          ref={chatShellRef}
          className="flex-1"
          {...(!isWeb && {
            onLayout: () => {
              chatShellRef.current?.measureInWindow?.((_x, y) => {
                if (y > 0) {
                  setChatKeyboardOffset(y);
                }
              });
            },
          })}>
          <ChatContent
            conversationStatus={currentConversationStatus}
            userId={userId}
            conversation={currentConversation}
            recipient={currentRecipient}
            inquiry={currentInquiry}
            isAuthor={isAuthor}
            closedConversation={closedConversation}
            draftMessage={draftMessage}
            setDraftMessage={setDraftMessage}
            clearDraft={clearDraft}
            keyboardVerticalOffset={chatKeyboardOffset}
            isResolvingActiveConversation={isResolvingActiveConversation}
          />
        </VStack>
      )}
    </VStack>
  );
}

function Container({
  id,
  allInquiries,
  tabSelected,
}: {
  id: string;
  allInquiries: InquiryV2[];
  tabSelected: string;
}) {
  const userId = useAtomValue(userIdAtom);

  const { conversation, recipient, conversationStatus, inquiry, error, retry } = useChat(id);

  if (error) {
    return <ChatLoadError errorType={error.type} onRetry={retry} />;
  }

  if (!userId || !conversation || !recipient || !conversationStatus || !inquiry) {
    return (
      <VStack className="flex-1">
        <Spinner size="large" className="absolute bottom-0 left-0 right-0 top-0 flex-1" />
      </VStack>
    );
  }

  return (
    <ChatErrorBoundary conversationId={id}>
      <Chat
        conversationStatus={conversationStatus}
        userId={userId}
        conversation={conversation}
        recipient={recipient}
        inquiry={inquiry}
        allInquiries={allInquiries}
        tabSelected={tabSelected}
      />
    </ChatErrorBoundary>
  );
}

type ChatSectionProps = {
  id?: string;
  openInquiriesCount: number;
  inquiriesClosedLastWeekCount: number;
  inquiriesCount: number;
  isLoading: boolean;
  allInquiries: InquiryV2[];
  tabSelected: string;
};

/**
 * Determines whether the Chat component tree should remain mounted.
 *
 * On web, the feedback modal (rating + success screens) is rendered inside
 * the Chat component tree via `webInquiryModals`. When a user closes their
 * last open inquiry, cache invalidation drops `inquiriesCount` to 0 and
 * clears the selected chat ID — which would normally render `<Empty />` or
 * `<Unselected />`, unmounting Chat and tearing down the feedback modal
 * mid-flow.
 *
 * This guard keeps the Chat tree mounted while the close-and-feedback
 * state machine is in the `closing` or `awaitingFeedback` phase.
 */
function shouldKeepChatMounted(
  inquiriesCount: number,
  id: string | undefined,
  isFeedbackActive: boolean
): boolean {
  if (!isFeedbackActive) {
    return false;
  }

  return !inquiriesCount || !id;
}

export function ChatSection({
  id,
  openInquiriesCount,
  inquiriesClosedLastWeekCount,
  inquiriesCount,
  isLoading,
  allInquiries,
  tabSelected,
}: ChatSectionProps) {
  const isFeedbackActive = useAtomValue(isFeedbackActiveAtom);

  const canStartSupportThread = useCanStartSupportThread();

  const conversationKind = useConversationKind(id);

  const keepMounted = shouldKeepChatMounted(inquiriesCount, id, isFeedbackActive);

  /**
   * Primum Support opens inquiries it is not part of from a support thread's
   * linked-inquiry card, and has none of its own, so a selected inquiry still
   * renders for it with an empty list. Anyone else with no inquiries keeps the
   * empty state, even with a stale id in the address.
   */
  const isOpeningLinkedInquiry = !!id && canStartSupportThread;

  if (isLoading || conversationKind === 'pending') {
    return <Spinner size="large" className="absolute bottom-0 left-0 right-0 top-0 flex-1" />;
  }

  if (id && conversationKind === 'support') {
    return <SupportThreadPane conversationId={id} />;
  }

  if (!inquiriesCount && !isOpeningLinkedInquiry && !keepMounted) {
    return <Empty />;
  }

  if (!id && !keepMounted) {
    return (
      <Unselected
        openInquiriesCount={openInquiriesCount}
        inquiriesClosedLastWeekCount={inquiriesClosedLastWeekCount}
      />
    );
  }

  if (!id) {
    return null;
  }

  return <Container id={id} allInquiries={allInquiries} tabSelected={tabSelected} />;
}
